import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckIcon,
  LockClosedIcon,
  PlusIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, Input, LoadingSpinner, Modal, ModalFooter, SearchableSelect } from '@/components/common';
import {
  useBulkSetModuleRights,
  useEffectiveRights,
  useRightsModulesCatalog,
  useRightsUsers,
} from '@/hooks/useEmployeeRights';
import { useCreateUser } from '@/hooks/useUsers';
import { getErrorMessage } from '@/services/api';
import {
  RIGHTS_LEVELS,
  RIGHTS_LEVEL_LABELS,
  RIGHTS_MODULES,
  RIGHTS_MODULE_LABELS,
  describeBulkSetError,
  type EffectiveRightsResponse,
  type ModuleOverrides,
  type RightsLevel,
  type RightsModule,
  type RightsSource,
  type RightsSubjectType,
  type RightsUserType,
} from '@/services/employeeRights';
import type { UserRole } from '@/types';
import { cn } from '@/utils';

// Point 11: labels for the four user types the Rights Management page lists.
const USER_TYPE_LABELS: Record<RightsUserType, string> = {
  employee: 'Employee',
  posp: 'POSP',
  advertiser: 'Advertiser',
  consolidator: 'Consolidator',
};

// B6: POSPs are now editable too -- `POSPRight` gives a POSP grant somewhere to
// persist, which is what the old "view-only for now" note was waiting on.
type Tab = 'employees' | 'posps';

export default function RightsManagementPage() {
  const [tab, setTab] = useState<Tab>('employees');
  const [showAddEmployee, setShowAddEmployee] = useState(false);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rights Management"
        subtitle="Create employees and set what each employee and POSP may read, create, edit and delete"
        actions={
          <Button leftIcon={<PlusIcon className="h-5 w-5" />} onClick={() => setShowAddEmployee(true)}>
            Add Employee
          </Button>
        }
      />

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6">
          <TabButton active={tab === 'employees'} onClick={() => setTab('employees')} icon={ShieldCheckIcon}>
            Employee Rights
          </TabButton>
          <TabButton active={tab === 'posps'} onClick={() => setTab('posps')} icon={UserGroupIcon}>
            POSP Rights
          </TabButton>
        </nav>
      </div>

      {tab === 'employees' && <RightsMatrixPanel key="employee" userType="employee" />}
      {tab === 'posps' && <RightsMatrixPanel key="posp" userType="posp" />}

      <AddEmployeeModal isOpen={showAddEmployee} onClose={() => setShowAddEmployee(false)} />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors',
        active
          ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      )}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );
}

// =============================================================================
// B4: the module x level matrix -- one subject at a time
// =============================================================================

type CellChoice = 'inherit' | 'grant' | 'deny';

const SOURCE_BADGES: Record<RightsSource, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' }> = {
  override: { label: 'Override', variant: 'primary' },
  legacy: { label: 'Legacy', variant: 'info' },
  role_default: { label: 'Department default', variant: 'default' },
  baseline: { label: 'Baseline', variant: 'default' },
  locked: { label: 'Locked', variant: 'warning' },
  // The subject's own login role already holds everything; their rows are never
  // read. Shown so the reader knows why every cell is on and nothing is editable.
  admin: { label: 'Admin role', variant: 'success' },
  none: { label: 'Not granted', variant: 'default' },
};

/**
 * What the subject's OWN rows say for a cell. `override` is a module row and
 * `legacy` an old per-code row on this same subject -- both explicit settings.
 * Anything else (department default, baseline, nothing) is "inherit".
 */
function savedChoice(effective: EffectiveRightsResponse | undefined, module: RightsModule, level: RightsLevel): CellChoice {
  const source = effective?.sources?.[module]?.[level];
  if (source !== 'override' && source !== 'legacy') return 'inherit';
  return effective?.modules?.[module]?.[level] ? 'grant' : 'deny';
}

function choiceToValue(choice: CellChoice): boolean | null {
  if (choice === 'inherit') return null;
  return choice === 'grant';
}

function countDraft(draft: ModuleOverrides): number {
  return Object.values(draft).reduce((total, row) => total + Object.keys(row ?? {}).length, 0);
}

/**
 * B4: rights are now a 10 x 4 grid (module x Read/Create/Edit/Delete) instead of
 * seven on/off codes.
 *
 * Each cell is tri-state:
 *  - Grant / Deny write an explicit override for this person;
 *  - Inherit REMOVES the override (`null` on bulk-set), so the cell falls back
 *    to the department default or the baseline. The old toggle list could not
 *    express this: every save wrote an explicit true/false for every code.
 *
 * What is drawn under each control is what the SERVER resolved
 * (`/employee-rights/effective/`), with where it came from. Cells a POSP can
 * never hold come from `/employee-rights/modules/` and are disabled; the API
 * refuses them independently.
 *
 * Unsaved choices live in their own `draft` map, separate from the fetched
 * matrix, so a background refetch can never discard an edit (BROKER-8a).
 */
function RightsMatrixPanel({ userType }: { userType: RightsSubjectType }) {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [draft, setDraft] = useState<ModuleOverrides>({});

  const usersQuery = useRightsUsers();
  const catalogQuery = useRightsModulesCatalog();

  const userOptions = useMemo(
    () => (usersQuery.data ?? []).filter((u) => u.user_type === userType),
    [usersQuery.data, userType]
  );

  // Auto-select the first user of this type once the list loads.
  useEffect(() => {
    if (!selectedUserId && userOptions.length > 0) {
      setSelectedUserId(userOptions[0].id);
    }
  }, [userOptions, selectedUserId]);

  const effectiveQuery = useEffectiveRights(userType, selectedUserId || undefined);
  const bulkSet = useBulkSetModuleRights();
  const effective = effectiveQuery.data;
  const catalog = catalogQuery.data;

  const selectUser = (id: string) => {
    setSelectedUserId(id);
    setDraft({});
  };

  const moduleRows = useMemo(
    () =>
      RIGHTS_MODULES.map((code) => ({
        code,
        label: catalog?.modules?.find((m) => m.code === code)?.label ?? RIGHTS_MODULE_LABELS[code],
      })),
    [catalog]
  );
  const levelColumns = useMemo(
    () =>
      RIGHTS_LEVELS.map((code) => ({
        code,
        label: catalog?.levels?.find((l) => l.code === code)?.label ?? RIGHTS_LEVEL_LABELS[code],
      })),
    [catalog]
  );

  const isLocked = (module: RightsModule, level: RightsLevel) =>
    (catalog?.locked?.[userType]?.[module] ?? []).includes(level) ||
    effective?.sources?.[module]?.[level] === 'locked';

  const currentChoice = (module: RightsModule, level: RightsLevel): CellChoice => {
    const pending = draft[module]?.[level];
    if (pending !== undefined) return pending === null ? 'inherit' : pending ? 'grant' : 'deny';
    return savedChoice(effective, module, level);
  };

  const setCell = (module: RightsModule, level: RightsLevel, choice: CellChoice) => {
    // A refusal of the LAST save says nothing about the cell being edited now;
    // leaving it up would have the save bar contradict itself.
    if (bulkSet.isError) bulkSet.reset();
    setDraft((prev) => {
      const next: ModuleOverrides = { ...prev };
      const row = { ...(next[module] ?? {}) };
      if (choice === savedChoice(effective, module, level)) {
        delete row[level];
      } else {
        row[level] = choiceToValue(choice);
      }
      if (Object.keys(row).length === 0) delete next[module];
      else next[module] = row;
      return next;
    });
  };

  const handleSave = () => {
    if (!selectedUserId || countDraft(draft) === 0) return;
    // Only `onSuccess` clears the draft, so a refused save keeps every choice
    // exactly as typed. What was missing is the other half: the failure was
    // invisible on the page (only a toast, which has usually gone by the time
    // anyone looks), so the save bar still read "3 unsaved changes" with no
    // reason -- indistinguishable from never having pressed Save.
    bulkSet.mutate(
      { subjectType: userType, subjectId: selectedUserId, modules: draft },
      { onSuccess: () => setDraft({}) }
    );
  };

  /**
   * The server's own words for a refused save.
   *
   * A POSP-locked cell comes back as `{"modules": {"policies": {"create":
   * "A POSP cannot hold this right."}}}` -- a body with no top-level
   * `error`/`detail`/`message`/`errors` key, which `getErrorMessage` cannot
   * read, so it would otherwise show axios's "Request failed with status code
   * 400". `describeBulkSetError` is the reader for that shape;
   * `getErrorMessage` stays the fallback for every ordinary failure.
   */
  const saveError = bulkSet.isError
    ? describeBulkSetError(
        (bulkSet.error as { response?: { data?: unknown } } | null | undefined)?.response?.data
      ) ?? getErrorMessage(bulkSet.error)
    : null;

  if (usersQuery.isLoading || catalogQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (usersQuery.isError) {
    return (
      <LoadError
        title="Could not load users"
        error={usersQuery.error}
        onRetry={() => usersQuery.refetch()}
      />
    );
  }

  if (catalogQuery.isError || !catalog) {
    return (
      <LoadError
        title="Could not load the rights catalog"
        error={catalogQuery.error}
        onRetry={() => catalogQuery.refetch()}
      />
    );
  }

  const pendingCount = countDraft(draft);
  const typeLabel = USER_TYPE_LABELS[userType].toLowerCase();

  return (
    <div className="space-y-6 pb-24">
      <Card className="p-6 space-y-4 max-w-2xl">
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">User</h3>
        </div>

        {userOptions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {userType === 'employee'
              ? 'No employees found for this broker. Use "Add Employee" above to create one.'
              : 'No POSPs found for this broker.'}
          </p>
        ) : (
          <SearchableSelect
            label="Select User"
            value={selectedUserId}
            onChange={selectUser}
            options={userOptions.map((u) => ({
              value: u.id,
              label: `${u.name || u.email || 'Unnamed'} (${USER_TYPE_LABELS[u.user_type]})`,
            }))}
          />
        )}
      </Card>

      {selectedUserId && (
        <Card className="p-4 sm:p-6 space-y-4">
          <div className="space-y-1">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Rights</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Grant or deny each action for this {typeLabel}. <strong>Inherit</strong> removes the
              override, so the {userType === 'employee' ? 'department default or ' : ''}baseline
              applies. Read only means they can view a module but not add, change or delete
              anything in it. Changes reach a signed-in user within a minute.
            </p>
          </div>

          {effectiveQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : effectiveQuery.isError || !effective ? (
            <LoadError
              title={`Could not load this ${typeLabel}'s rights`}
              error={effectiveQuery.error}
              onRetry={() => effectiveQuery.refetch()}
            />
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <caption className="sr-only">
                  Module rights for the selected {typeLabel}
                </caption>
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                      Module
                    </th>
                    {levelColumns.map((level) => (
                      <th
                        key={level.code}
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300"
                      >
                        {level.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {moduleRows.map((module) => {
                    const baselineLevels = catalog.baseline?.[userType]?.[module.code] ?? [];
                    return (
                      <tr key={module.code} data-testid={`rights-row-${module.code}`}>
                        <th scope="row" className="px-4 py-3 text-left align-top">
                          <span className="block font-medium text-gray-900 dark:text-white">{module.label}</span>
                          <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                            Baseline:{' '}
                            {baselineLevels.length > 0
                              ? baselineLevels.map((l) => RIGHTS_LEVEL_LABELS[l] ?? l).join(', ')
                              : 'none'}
                          </span>
                        </th>
                        {levelColumns.map((level) => (
                          <MatrixCell
                            key={level.code}
                            label={`${module.label} ${level.label}`}
                            choice={currentChoice(module.code, level.code)}
                            locked={isLocked(module.code, level.code)}
                            dirty={draft[module.code]?.[level.code] !== undefined}
                            allowed={effective.modules?.[module.code]?.[level.code] === true}
                            source={effective.sources?.[module.code]?.[level.code] ?? 'none'}
                            disabled={bulkSet.isPending}
                            onChange={(choice) => setCell(module.code, level.code, choice)}
                          />
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {pendingCount > 0 && (
        <SaveBar
          pendingCount={pendingCount}
          onDiscard={() => setDraft({})}
          onSave={handleSave}
          isSaving={bulkSet.isPending}
          error={saveError}
        />
      )}
    </div>
  );
}

function MatrixCell({
  label,
  choice,
  locked,
  dirty,
  allowed,
  source,
  disabled,
  onChange,
}: {
  label: string;
  choice: CellChoice;
  locked: boolean;
  dirty: boolean;
  allowed: boolean;
  source: RightsSource;
  disabled: boolean;
  onChange: (choice: CellChoice) => void;
}) {
  const badge = SOURCE_BADGES[source] ?? SOURCE_BADGES.none;
  return (
    <td className="px-3 py-3 align-top">
      <div className="flex min-w-[7.5rem] flex-col gap-1.5">
        {locked ? (
          <select
            aria-label={label}
            disabled
            value="locked"
            className="w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-100 px-2 py-1 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
          >
            <option value="locked">Not available</option>
          </select>
        ) : (
          <select
            aria-label={label}
            value={choice}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value as CellChoice)}
            className={cn(
              'w-full rounded-md border bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-900 dark:text-white',
              dirty
                ? 'border-primary-500 ring-1 ring-primary-500'
                : 'border-gray-300 dark:border-gray-600'
            )}
          >
            <option value="inherit">Inherit</option>
            <option value="grant">Grant</option>
            <option value="deny">Deny</option>
          </select>
        )}
        {dirty ? (
          <span className="text-xs font-medium text-primary-700 dark:text-primary-400">Unsaved</span>
        ) : (
          <span className="flex flex-wrap items-center gap-1 text-xs">
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium',
                allowed ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
              )}
            >
              {allowed ? (
                <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {allowed ? 'Allowed' : 'Denied'}
            </span>
            <Badge size="sm" variant={badge.variant}>
              {source === 'locked' && <LockClosedIcon className="mr-0.5 h-3 w-3" aria-hidden="true" />}
              {badge.label}
            </Badge>
          </span>
        )}
      </div>
    </td>
  );
}

function LoadError({ title, error, onRetry }: { title: string; error: unknown; onRetry: () => void }) {
  return (
    <Card className="p-6">
      <div role="alert" className="space-y-3">
        <p className="text-sm font-medium text-red-700 dark:text-red-400">{title}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{getErrorMessage(error)}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </Card>
  );
}

/**
 * B6: "Brokers should be able to create their own employees."
 *
 * Nothing in the broker UI created a BrokerEmployee before -- Rights Management
 * could only list rows that already existed, so a broker had to ask the Country
 * Admin for every new employee.
 *
 * `tenant_id` is deliberately NOT sent. `AdminUserCreateSerializer.create`
 * assigns it from the requesting broker admin, and its `validate` restricts a
 * broker_admin to broker_employee/posp roles. Sending a tenant from the client
 * would be the client asserting which broker owns the new user, which is
 * exactly the decision the server should be making.
 */
function AddEmployeeModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const emptyForm = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
  };
  const [form, setForm] = useState(emptyForm);
  const createUser = useCreateUser();
  const queryClient = useQueryClient();

  const canSubmit =
    !!form.first_name && !!form.last_name && !!form.phone && !!form.password && !!form.email;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createUser.mutate(
      { ...form, role: 'broker_employee' as UserRole },
      {
        onSuccess: () => {
          // The new employee must appear in the selector straight away.
          queryClient.invalidateQueries({ queryKey: ['rights-users'] });
          setForm(emptyForm);
          onClose();
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Employee">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={form.first_name}
            onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
            required
          />
          <Input
            label="Last Name"
            value={form.last_name}
            onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
            required
          />
        </div>
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          required
        />
        <Input
          label="Mobile Number"
          inputMode="numeric"
          value={form.phone}
          onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
          placeholder="10-digit mobile number"
          required
        />
        <Input
          label="Temporary Password"
          type="password"
          value={form.password}
          onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
          placeholder="At least 8 characters"
          required
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The employee is created under your broker and can sign in with these details.
          Set their rights on the Employee Rights tab.
        </p>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} isLoading={createUser.isPending} disabled={!canSubmit}>
          Create Employee
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function SaveBar({
  pendingCount,
  onDiscard,
  onSave,
  isSaving,
  error,
}: {
  pendingCount: number;
  onDiscard: () => void;
  onSave: () => void;
  isSaving: boolean;
  /** Why the last save was refused, in the server's words. The draft is kept. */
  error?: string | null;
}) {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] shadow-lg z-50">
      {error && (
        <p
          role="alert"
          className="mb-3 text-sm text-red-700 dark:text-red-400 sm:text-right"
        >
          Not saved: {error} Your changes are still here — fix the refused cell and save again.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {pendingCount} unsaved {pendingCount === 1 ? 'change' : 'changes'}
        </span>
        <Button variant="secondary" onClick={onDiscard}>
          Discard
        </Button>
        <Button onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
